import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { InputNode } from '../types';
import { parseStepsTooltip } from '../utils/stepsTooltip';
import { StepsTutorial } from './StepsTutorial';
import { InfoIconTooltip } from './InfoIconTooltip';

type VariableNodeProps = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  error?: string;
};

const normalizeTooltip = (tooltip: InputNode['tooltip']): string => {
  if (!tooltip) return '';
  return typeof tooltip === 'string' ? tooltip : JSON.stringify(tooltip);
};

/**
 * StructuredEditor 的 variable chip。
 *
 * 实现：原生 <textarea> + 隐藏 mirror <span>，靠 inline-grid 同格叠放，
 * 由 mirror 内容自然撑开 grid cell。
 *
 * 多行 / 独占一行的两条触发路径合一到 data-multiline="1"：
 *   1. 手动 Enter（text 含 \n）→ 同步派生
 *   2. 字数物理触底自动换行（mirror.offsetHeight 超过单行）→ 异步 measure
 * 一旦命中 multiline，CSS 切 display:grid + margin: 4px 0 + width:100% 独占整行
 * + 上下 4px 间距。
 *
 * 关键：编辑器宽度变化时允许 isOverflow 双向同步；节点自身高度变化引起的
 * ResizeObserver 回调不重复测量，避免 multiline 布局切换形成反馈循环。
 */
export const VariableNode: React.FC<VariableNodeProps> = React.memo(
  ({ node, value, onChange, disabled, error }) => {
    const tooltip = normalizeTooltip(node.tooltip);
    const text = value ?? '';
    const placeholder = node.placeholder || '';
    const hasValue = text.length > 0;
    // steps tooltip 走「查看教程」按钮，chip 内不再渲染 info-icon（避免两个触发点）。
    // 普通文案 tooltip 才在 chip 内显示 info-icon。
    const stepsTooltip = parseStepsTooltip(node.tooltip);
    const isSteps = !!stepsTooltip;
    const showInfoIcon = Boolean(tooltip) && !isSteps && !hasValue;
    const showInChipIcon = Boolean(tooltip) && !isSteps;
    const isPasteText = node.type === 'paste-text';

    const hostRef = useRef<HTMLSpanElement>(null);
    const mirrorRef = useRef<HTMLSpanElement>(null);
    const prevTextRef = useRef(text);
    const [isOverflow, setIsOverflow] = useState<boolean>(() =>
      text.includes('\n'),
    );

    // allowNewline 不传默认 true（允许换行）；显式 false 时单行变量：
    // 折叠所有换行为空格（覆盖输入法 / 粘贴 / 拖拽多路径，无需单独 onPaste）。
    const allowNewline = isPasteText || node.allowNewline !== false;

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (!node.key) return;
        const raw = e.target.value;
        const next = allowNewline ? raw : raw.replace(/\n+/g, ' ');
        onChange(node.key, next);
      },
      [node.key, onChange, allowNewline],
    );

    // allowNewline=false 时拦截 Enter，只阻止换行、不触发发送（V1 parity）；
    // IME 合成期（中文/日文候选未上屏）放行，让回车去确认候选而非被吞。
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (allowNewline) return;
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;
        if (e.key === 'Enter') e.preventDefault();
      },
      [allowNewline],
    );

    /** 测 mirror 实际高度是否超过单行高度。upgradeOnly=true 时只升不降。 */
    const measure = useCallback(
      (upgradeOnly: boolean) => {
        const mirror = mirrorRef.current;
        if (!mirror) return;
        const cs = window.getComputedStyle(mirror);
        const lineHeight = Number.parseFloat(cs.lineHeight) || 22;
        const padTop = Number.parseFloat(cs.paddingTop) || 0;
        const padBottom = Number.parseFloat(cs.paddingBottom) || 0;
        const singleLineHeight = lineHeight + padTop + padBottom;
        const measured = mirror.offsetHeight > singleLineHeight + 1;
        const next = text.includes('\n') || measured;
        if (upgradeOnly) {
          if (next) setIsOverflow((prev) => prev || true);
        } else {
          setIsOverflow((prev) => (prev === next ? prev : next));
        }
      },
      [text],
    );

    // text 变化时：先按 \n 重置 isOverflow（允许降级），再 measure 看是否要升回 true
    useLayoutEffect(() => {
      if (prevTextRef.current !== text) {
        prevTextRef.current = text;
        measure(false);
      } else {
        measure(true);
      }
    }, [measure, text, placeholder]);

    // 仅响应编辑器宽度变化，允许 chip 在缩窄/拉宽时进入或退出 multiline。
    // multiline 切换只改变编辑器高度时跳过，避免 ResizeObserver 反馈循环。
    useEffect(() => {
      const host = hostRef.current;
      if (!host || typeof ResizeObserver === 'undefined') return;
      const editorEl = host.closest<HTMLElement>('.rte-editor');
      if (!editorEl) return;
      let lastWidth = editorEl.getBoundingClientRect().width;
      const ro = new ResizeObserver(([entry]) => {
        const width = entry?.contentRect.width ?? editorEl.getBoundingClientRect().width;
        if (Math.abs(width - lastWidth) < 0.5) return;
        lastWidth = width;
        measure(false);
      });
      ro.observe(editorEl);
      return () => ro.disconnect();
    }, [measure]);

    const mirrorText = (hasValue ? text : placeholder) + ' ';

    const hostEl = (
      <span
        ref={hostRef}
        className="se-variable"
        data-type={node.type}
        data-key={node.key}
        data-group={node.exclusionGroup}
        data-tooltip={tooltip || undefined}
        data-variable-key={tooltip ? node.key : undefined}
        data-error={error ? '1' : undefined}
        data-has-icon={showInfoIcon ? '1' : undefined}
        data-multiline={isOverflow ? '1' : undefined}
      >
        <span ref={mirrorRef} className="se-variable-mirror" aria-hidden="true">
          {mirrorText}
        </span>
        <textarea
          cols={1}
          rows={1}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={!!error}
        />
        {showInChipIcon && (
          <InfoIconTooltip
            tooltip={tooltip}
            className="variable-info-icon"
            hidden={!showInfoIcon}
            dataVariableKey={node.key}
          />
        )}
      </span>
    );

    // steps tooltip：在 chip 同级渲染「查看教程」按钮（已填值时隐藏，与 V1 一致）
    const withTutorial = isSteps ? (
      <>
        {hostEl}
        <StepsTutorial data={stepsTooltip} hidden={hasValue} />
      </>
    ) : (
      hostEl
    );

    if (!node.required) return withTutorial;

    return (
      <span
        className="exclusion-wrapper required-upload-group"
        data-group={node.exclusionGroup}
      >
        <span className="rte-text-required">
          <i className="iconfont icon-bitian" style={{ fontSize: 10 }} />
        </span>
        {withTutorial}
      </span>
    );
  },
);
VariableNode.displayName = 'VariableNode';
